import { createSignal, createMemo, onCleanup, onMount, For, Show } from "solid-js";
import { A } from "@solidjs/router";
import { createStore, reconcile } from "solid-js/store";
import { useApi, isOk } from "../api";
import { useUserContext } from "../components/UserContextProvider";
import { useRoleParser } from "../hooks/permissions";
import BotStatusBadge from "../components/BotStatusBadge";
import Text from "../components/Text";
import { SearchInput } from "../components/SearchInput";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./BotRegistryAdmin.module.css";

const POLL_MS = 10_000;

interface AdminBotRow {
  id: string;
  minecraftUuid: string;
  minecraftUsername: string;
  ownerUserId: string;
  ownerUsername: string;
  proxyId: string | null;
  proxyName: string | null;
  state: string;
}

export default function BotRegistryAdmin() {
  const api = useApi();
  const { user } = useUserContext();
  const { parseRoles, roles } = useRoleParser();
  const { t } = useTranslation();

  const isPlatformAdmin = () => {
    const flags = parseRoles(user()?.permissions ?? 0, roles);
    return flags[roles.indexOf("Root")] ?? false;
  };

  const [bots, setBots] = createStore<AdminBotRow[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [search, setSearch] = createSignal("");

  async function fetchBots() {
    const res = await api.botRegistry.listAllBotsForAdmin();
    if (isOk(res.data)) setBots(reconcile(res.data.value.bots as unknown as AdminBotRow[], { key: "id" }));
    setLoading(false);
  }

  let pollTimer: number | undefined;
  onMount(() => {
    fetchBots();
    pollTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchBots();
    }, POLL_MS);
  });
  onCleanup(() => {
    if (pollTimer) window.clearInterval(pollTimer);
  });

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    if (!q) return bots;
    return bots.filter(b =>
      b.minecraftUsername.toLowerCase().includes(q)
      || b.minecraftUuid.toLowerCase().includes(q)
      || b.ownerUsername.toLowerCase().includes(q)
      || (b.proxyName ?? "Direct").toLowerCase().includes(q));
  });

  const onlineCount = createMemo(() => bots.filter(b => b.state === "Playing").length);

  return (
    <Show when={isPlatformAdmin()} fallback={
      <div class={styles.forbidden}>
        <h1><Text val="FORBIDDEN" /></h1>
        <p><Text val="PLATFORM_ADMIN_ONLY" /></p>
      </div>
    }>
      <div class={styles.container}>
        <div class={styles.headerRow}>
          <h1><Text val="BOT_REGISTRY_ADMIN_TITLE" /></h1>
          <span class={styles.entryCount}>
            {bots.length} <Text val="BOT_REGISTRY_ADMIN_TOTAL" /> &middot; {onlineCount()} <Text val="BOT_REGISTRY_ADMIN_ONLINE" />
          </span>
        </div>

        <div class={styles.toolbar}>
          <SearchInput
            value={search()}
            onInput={setSearch}
            placeholder={t('BOT_REGISTRY_ADMIN_SEARCH_PLACEHOLDER')}
          />
        </div>

        <Show when={!loading()} fallback={<div class={styles.empty}><Text val="LOADING" /></div>}>
          <Show when={filtered().length > 0} fallback={
            <div class={styles.empty}><Text val="BOT_REGISTRY_ADMIN_EMPTY" /></div>
          }>
            <div class={styles.list}>
              <For each={filtered()}>{(b) => (
                <div class={`fcard ${styles.row}`}>
                  <div class={styles.identity}>
                    <BotStatusBadge state={b.state} compact />
                    <div class={styles.names}>
                      <span class={styles.username}>{b.minecraftUsername}</span>
                      <span class={styles.uuid}>{b.minecraftUuid}</span>
                    </div>
                  </div>
                  <div class={styles.meta}>
                    <span class={styles.metaItem} title={t('ADMIN_BOT_OWNER')}>
                      <i class="fa-solid fa-user" /> {b.ownerUsername}
                    </span>
                    <span class={styles.metaItem} title={b.proxyName ?? t('PROXY_DIRECT_SHORT')}>
                      <i class={`fa-solid ${b.proxyId ? "fa-network-wired" : "fa-globe"}`} />
                      {b.proxyName ?? <Text val="PROXY_DIRECT_SHORT" />}
                    </span>
                    <A class="btn btn-secondary btn-sm" href={`/botengine?bot=${b.id}&all=1`}>
                      <i class="fa-solid fa-pen-to-square" /> <Text val="ADMIN_BOT_MANAGE" />
                    </A>
                  </div>
                </div>
              )}</For>
            </div>
          </Show>
        </Show>
      </div>
    </Show>
  );
}
